import { Trans, useTranslation } from 'react-i18next';
import { useState, useCallback } from 'react';
import { useNavigate } from '@remix-run/react';
import { isInquiryModeEnabled, onOpenInquiry } from '../state/inquiry-modal';

export const KumbhSukritHeader = () => {
  const { t } = useTranslation(['home', 'common']);
  const navigate = useNavigate();
  const [checkIn, setCheckIn] = useState('');
  const [checkOut, setCheckOut] = useState('');
  const [guests, setGuests] = useState('2');

  // When booking mode is enabled, just navigate to booking page
  // When inquiry mode is enabled, open inquiry modal with optional date preferences
  const onBookingClick = useCallback(() => {
    if (isInquiryModeEnabled) {
      onOpenInquiry({
        checkIn: checkIn || undefined,
        checkOut: checkOut || undefined,
        guests: guests ? parseInt(guests, 10) : undefined,
      });
    } else {
      navigate('/packages/reservation');
    }
  }, [checkIn, checkOut, guests, navigate]);

  return (
    <section className="w-full relative min-h-[680px] sm:min-h-[850px] md:min-h-[920px] lg:h-[1050px] xl:h-[1400px] max-h-[100dvh]">
      {/* Video Background */}
      {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
      <video
        controls={false}
        src="https://pub-3acbfdb36a6a4a758770a8294a62150d.r2.dev/ks-montage-v2-compressed.mp4"
        className="object-cover w-full h-full absolute inset-0 z-0"
        autoPlay={true}
        muted={true}
        loop={true}
        playsInline={true}
      />

      {/* Gradient Overlays */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/20 to-black/40 z-[1]" />

      {/* Hero Content - Positioned lower */}
      <div className="absolute inset-0 flex flex-col items-center justify-end pb-40 sm:pb-48 md:pb-56 px-6 sm:px-12 md:px-24 z-[2]">
        <div className="text-center max-w-4xl mx-auto">
          {/* Event Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-md border border-white/20 mb-6">
            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
            <span className="text-white/90 text-sm font-medium tracking-wide">
              {t('common:event-badge')}
            </span>
          </div>

          {/* Main Heading */}
          <h1 className="text-white font-bold text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-display leading-tight">
            <Trans
              i18nKey="home:header.celebrate"
              components={{
                highlight: <span className="text-emerald-300" />,
              }}
            />
          </h1>

          {/* Subtitle */}
          <p className="text-white/90 text-base sm:text-lg md:text-xl mt-6 max-w-2xl mx-auto leading-relaxed">
            <Trans
              i18nKey="home:header.join"
              components={{
                br: <br className="hidden sm:block" />,
              }}
            />
          </p>

          {/* Mobile CTA */}
          <div className="md:hidden mt-8 flex flex-col items-center gap-3">
            {isInquiryModeEnabled ? (
              <button
                onClick={onBookingClick}
                className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white font-semibold px-8 py-3.5 rounded-xl transition-all shadow-lg hover:shadow-xl hover:scale-105"
              >
                {t('common:express-interest')} →
              </button>
            ) : (
              <>
                <button
                  onClick={onBookingClick}
                  className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white font-semibold px-8 py-3.5 rounded-xl transition-all shadow-lg hover:shadow-xl hover:scale-105"
                >
                  {t('common:book-now')} →
                </button>
                <button
                  onClick={() => onOpenInquiry()}
                  data-testid="express-interest-button"
                  className="bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white font-medium px-6 py-2.5 rounded-lg transition-all border border-white/30"
                >
                  {t('common:express-interest')}
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Bottom Bar - Fixed at bottom */}
      <div className="absolute bottom-0 left-0 right-0 z-[3]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          {isInquiryModeEnabled ? (
            <>
              {/* Desktop Inquiry Bar with date picker */}
              <div className="hidden md:block bg-white/95 backdrop-blur-md rounded-t-2xl shadow-2xl border border-white/50 overflow-hidden">
                <div className="p-4 md:p-5">
                  <div className="flex items-end gap-4">
                    {/* Check-in */}
                    <div className="flex-1 min-w-0">
                      <label
                        htmlFor="hero-checkin"
                        className="block text-xs font-semibold text-emerald-700 uppercase tracking-wide mb-1.5"
                      >
                        {t('common:check-in')}
                      </label>
                      <input
                        id="hero-checkin"
                        type="date"
                        value={checkIn}
                        onChange={(e) => setCheckIn(e.target.value)}
                        min="2025-12-31"
                        max="2026-02-20"
                        className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-gray-800 font-medium focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                      />
                    </div>

                    <div className="h-12 w-px bg-gray-200 hidden lg:block" />

                    {/* Check-out */}
                    <div className="flex-1 min-w-0">
                      <label
                        htmlFor="hero-checkout"
                        className="block text-xs font-semibold text-emerald-700 uppercase tracking-wide mb-1.5"
                      >
                        {t('common:check-out')}
                      </label>
                      <input
                        id="hero-checkout"
                        type="date"
                        value={checkOut}
                        onChange={(e) => setCheckOut(e.target.value)}
                        min={checkIn || '2025-12-31'}
                        max="2026-02-20"
                        className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-gray-800 font-medium focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                      />
                    </div>

                    <div className="h-12 w-px bg-gray-200 hidden lg:block" />

                    {/* Guests */}
                    <div className="w-32 lg:w-36">
                      <label
                        htmlFor="hero-guests"
                        className="block text-xs font-semibold text-emerald-700 uppercase tracking-wide mb-1.5"
                      >
                        {t('common:guests')}
                      </label>
                      <select
                        id="hero-guests"
                        value={guests}
                        onChange={(e) => setGuests(e.target.value)}
                        className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-gray-800 font-medium focus:ring-2 focus:ring-emerald-500 focus:border-transparent appearance-none cursor-pointer"
                        style={{
                          backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                          backgroundPosition: 'right 0.5rem center',
                          backgroundRepeat: 'no-repeat',
                          backgroundSize: '1.5em 1.5em',
                          paddingRight: '2.5rem',
                        }}
                      >
                        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
                          <option key={n} value={n}>
                            {t('common:guest', { count: n })}
                          </option>
                        ))}
                        <option value="11">{t('common:guests-10-plus')}</option>
                      </select>
                    </div>

                    {/* Express Interest Button */}
                    <button
                      onClick={onBookingClick}
                      data-testid="express-interest-button"
                      className="flex-shrink-0 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white font-semibold px-6 lg:px-8 py-3 rounded-xl transition-all shadow-lg hover:shadow-xl flex items-center gap-2"
                    >
                      <span>{t('common:express-interest')}</span>
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M14 5l7 7m0 0l-7 7m7-7H3"
                        />
                      </svg>
                    </button>
                  </div>

                  {/* Event Info Strip */}
                  <div className="flex items-center justify-center gap-6 mt-4 pt-4 border-t border-gray-100">
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <svg
                        className="w-4 h-4 text-emerald-500"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                        />
                      </svg>
                      <span>{t('common:event-dates')}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <svg
                        className="w-4 h-4 text-emerald-500"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                      </svg>
                      <span>{t('common:event-location')}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <svg
                        className="w-4 h-4 text-emerald-500"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                        />
                      </svg>
                      <span>{t('common:premium-eco-village')}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tablet Inquiry Bar (sm screens) */}
              <div className="hidden sm:block md:hidden bg-white/95 backdrop-blur-md rounded-t-2xl shadow-2xl border border-white/50 overflow-hidden">
                <div className="p-4">
                  <div className="grid grid-cols-3 gap-3 mb-3">
                    <div>
                      <label
                        htmlFor="hero-checkin-sm"
                        className="block text-xs font-semibold text-emerald-700 uppercase tracking-wide mb-1"
                      >
                        {t('common:check-in')}
                      </label>
                      <input
                        id="hero-checkin-sm"
                        type="date"
                        value={checkIn}
                        onChange={(e) => setCheckIn(e.target.value)}
                        min="2025-12-31"
                        max="2026-02-20"
                        className="w-full px-2 py-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-800 text-sm font-medium focus:ring-2 focus:ring-emerald-500"
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="hero-checkout-sm"
                        className="block text-xs font-semibold text-emerald-700 uppercase tracking-wide mb-1"
                      >
                        {t('common:check-out')}
                      </label>
                      <input
                        id="hero-checkout-sm"
                        type="date"
                        value={checkOut}
                        onChange={(e) => setCheckOut(e.target.value)}
                        min={checkIn || '2025-12-31'}
                        max="2026-02-20"
                        className="w-full px-2 py-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-800 text-sm font-medium focus:ring-2 focus:ring-emerald-500"
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="hero-guests-sm"
                        className="block text-xs font-semibold text-emerald-700 uppercase tracking-wide mb-1"
                      >
                        {t('common:guests')}
                      </label>
                      <select
                        id="hero-guests-sm"
                        value={guests}
                        onChange={(e) => setGuests(e.target.value)}
                        className="w-full px-2 py-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-800 text-sm font-medium focus:ring-2 focus:ring-emerald-500"
                      >
                        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
                          <option key={n} value={n}>
                            {t('common:guest', { count: n })}
                          </option>
                        ))}
                        <option value="11">{t('common:guests-10-plus')}</option>
                      </select>
                    </div>
                  </div>
                  <button
                    onClick={onBookingClick}
                    data-testid="express-interest-button"
                    className="w-full bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white font-semibold py-3 rounded-xl transition-all shadow-lg"
                  >
                    {t('common:express-interest')} →
                  </button>
                </div>
              </div>
            </>
          ) : (
            /* Book Now and Express Interest CTAs when booking mode is enabled */
            <div className="hidden sm:block bg-white/95 backdrop-blur-md rounded-t-2xl shadow-2xl border border-white/50 overflow-hidden">
              <div className="p-4 md:p-5">
                <div className="flex items-center justify-between gap-4">
                  {/* Event Info */}
                  <div className="flex items-center gap-6">
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <svg
                        className="w-4 h-4 text-emerald-500"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                        />
                      </svg>
                      <span>{t('common:event-dates')}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <svg
                        className="w-4 h-4 text-emerald-500"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                      </svg>
                      <span>{t('common:event-location')}</span>
                    </div>
                    <div className="hidden md:flex items-center gap-2 text-sm text-gray-600">
                      <svg
                        className="w-4 h-4 text-emerald-500"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                        />
                      </svg>
                      <span>{t('common:premium-eco-village')}</span>
                    </div>
                  </div>

                  {/* CTA Buttons */}
                  <div className="flex items-center gap-2">
                    {/* Express Interest Button */}
                    <button
                      onClick={() => onOpenInquiry()}
                      data-testid="express-interest-button"
                      className="flex-shrink-0 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium px-4 py-3 rounded-xl transition-all"
                    >
                      {t('common:express-interest')}
                    </button>
                    {/* Book Now Button */}
                    <button
                      onClick={onBookingClick}
                      className="flex-shrink-0 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white font-semibold px-8 py-3 rounded-xl transition-all shadow-lg hover:shadow-xl hover:scale-105 flex items-center gap-2"
                    >
                      <span>{t('common:book-now')}</span>
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M14 5l7 7m0 0l-7 7m7-7H3"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};
